// 인생 사건(질병·직업 전환·주택·결혼·출산·반려동물·이혼·재혼) 판정. 순수 함수이며 같은 시드·나이면 같은 결과가 나온다.
// 설계: docs/life-events-engine-design.md. 3층 구조: 빈도는 통계(lifeTables), 시기·성격은 사주(sajuFactor), 결과는 선택.
// 이혼·재혼은 통계가 아니라 사주로 정한 게임 추정이다. 사별은 다루지 않는다.
import TABLES from '../data/lifeTables.js';
import { LIFE_EVENT_CONTENT, LIFE_RECORD_TEXT } from '../data/lifeEventContent.js';
import { mulberry32 } from './rng.js';
import { GROUP_LABEL } from './rules.js';
import { housePurchase, fmtMoney, fillMoney } from './economy.js';
import { luckAt } from './luckPhase.js';
import { classifyTransition, getTransitionTable } from './luckTransition.js';
import { essenceFor } from './events.js';

export const LIFE_MODEL = 'stat-v1';
export const LIFE_TYPES = ['illness', 'jobChange', 'house', 'marriage', 'birth', 'pet', 'divorce', 'remarriage'];
// 대운 점수가 같을 때의 우선순위(앞이 먼저)
const PRIORITY = ['illness', 'jobChange', 'house', 'marriage', 'divorce', 'remarriage', 'birth', 'pet'];
const SALT = { illness: 201, jobChange: 202, house: 203, marriage: 204, birth: 205, pet: 206, divorce: 207, remarriage: 208, state: 220 };
const PET_COOLDOWN = 12; // 거절했거나 들인 뒤에는 한동안 입양 제안이 다시 나오지 않게 한다
// 제안이 반복되지 않게: 같은 종류의 제안 사이 최소 간격(년)과 한 생의 최대 제안 횟수(거절·실패도 센다). 모두 게임 추정이다.
const OFFER_SPACING = { house: 3, marriage: 3, divorce: 4, remarriage: 3, birth: 4 };
const OFFER_MAX = { house: 3, marriage: 3, divorce: 2, remarriage: 2, birth: 2 };
const offerOk = (life, type, age) => age - (life.lastOffer?.[type] ?? -99) >= (OFFER_SPACING[type] ?? 0) && (life.offerCount?.[type] ?? 0) < (OFFER_MAX[type] ?? 99);
const MAX_PET_OFFERS = 2; // 한 생에서 입양 제안은 최대 2번(게임 추정)
const MAX_PETS = 3; // 한 번에 키우는 반려동물 수의 상한(게임 추정)
const PET_LIFESPAN = [10, 15]; // 반려동물 수명(년, 사용자 지정). 입양할 때 10~15 중 하나로 정한다.
const petLifespan = (roll) => PET_LIFESPAN[0] + Math.floor(roll * (PET_LIFESPAN[1] - PET_LIFESPAN[0] + 1));
const petCount = (life) => life.pet?.list?.length ?? life.pet?.count ?? (life.pet?.owned ? 1 : 0);
const syncPets = (life) => { life.pet.count = life.pet.list.length; life.pet.owned = life.pet.count > 0; life.pet.since = life.pet.list[0]?.adopted ?? null; };
const MAX_PER_YEAR = 2; // 한 해 최대 2건: 1건은 선택 이벤트, 1건은 자동 기록
const lifeRng = (s, age, salt) => mulberry32((s.seed + age * 7919 + salt * 104729) >>> 0);

let tables = TABLES;
export const getLifeTables = () => tables;
export const setLifeTables = (t) => { tables = t ?? TABLES; };

export const initLife = () => ({
  version: 1, health: { state: null, since: null }, work: { state: null }, marriage: { status: 'single', at: null, divorces: 0 },
  children: [], lastOffer: {}, home: { count: 0, boughtAge: null }, pet: { owned: null, count: 0, since: null, list: [], offers: 0 }, occurrences: [],
});

// ───────── 사주 변조 ─────────
const BRANCH_HAP = [[0, 1], [2, 11], [3, 10], [4, 9], [5, 8], [6, 7]];
const isHap = (a, b) => BRANCH_HAP.some(([x, y]) => (a === x && b === y) || (a === y && b === x));
const isChung = (a, b) => (a + 6) % 12 === b;
const HYUNG = [[2, 5, 8], [1, 10, 7]];
const isHyung = (a, b) => a !== b && (HYUNG.some((g) => g.includes(a) && g.includes(b)) || (a === 0 && b === 3) || (a === 3 && b === 0));
const isClash = (a, b) => isChung(a, b) || isHyung(a, b);

// 사건 → 열린 영역(성별 구분). 1단계 후보 해를 고르는 기준이다.
const LINK = {
  marriage: { M: '재성', F: '관성' }, remarriage: { M: '재성', F: '관성' }, divorce: null,
  birth: { M: '관성', F: '식상' }, house: { M: '재성', F: '재성' }, jobChange: { M: '관성', F: '관성' }, pet: { M: '식상', F: '식상' },
};
export const OPEN_BOOST = 1.5;
const TRANSITION_EFFECT = {
  marriage: { 돕는: 1.15, 막는: 0.85 }, birth: { 돕는: 1.15, 막는: 0.85 }, house: { 돕는: 1.15, 막는: 0.85 }, pet: { 돕는: 1.1, 막는: 0.9 },
  jobChange: { 돕는: 0.9, 바꾸는: 1.25 }, divorce: { 막는: 1.5 }, remarriage: { 돕는: 1.5 },
};
const SPOUSE_GROUP = { M: '재성', F: '관성' };
export const SPOUSE_STAR_HIGH = 0.35, SPOUSE_STAR_LOW = 0.05;
const BRANCH_MAIN_YEAR = (year) => (((year - 4) % 12) + 12) % 12;

/** 정규화 전 사주 계수. opens 는 opensFor 결과(열린 영역 배열). */
export function rawFactor(s, age, type, opens) {
  const sex = s.birth?.gender === 'F' ? 'F' : 'M';
  let m = 1;
  const link = LINK[type]?.[sex];
  if (link && opens.includes(link)) m *= OPEN_BOOST;
  const luck = luckAt(s.saju, age);
  const eff = TRANSITION_EFFECT[type];
  if (eff && luck.inTransition && getTransitionTable()) {
    const kind = classifyTransition(essenceFor(s.saju), luck);
    if (kind && eff[kind]) m *= eff[kind];
  }
  if (type === 'marriage' || type === 'divorce' || type === 'remarriage') {
    const palace = s.saju?.day?.branch;
    const seun = BRANCH_MAIN_YEAR(s.birthYear + age);
    const dw = luck.daewoon?.branch;
    if (Number.isInteger(palace)) {
      const branches = [seun, dw].filter(Number.isInteger);
      if (type === 'marriage') {
        if (branches.some((b) => isHap(palace, b))) m *= 1.25;
        if (branches.some((b) => isClash(palace, b))) m *= 0.85;
      } else if (type === 'divorce') {
        if (branches.some((b) => isClash(palace, b))) m *= 2;
        if (branches.some((b) => isHap(palace, b))) m *= 0.8;
      } else if (branches.some((b) => isHap(palace, b))) m *= 1.5;
    }
  }
  if (type === 'divorce') {
    const share = essenceFor(s.saju)?.groupShare?.value?.[SPOUSE_GROUP[sex]];
    if (Number.isFinite(share) && (share >= SPOUSE_STAR_HIGH || share <= SPOUSE_STAR_LOW)) m *= 1.5;
  }
  return m;
}

// 통계 기반 사건의 사주 변조는 ±30% 안쪽, 통계 없이 사주로 정하는 이혼·재혼은 더 넓게 허용한다.
export const FACTOR_RANGE = { default: [0.7, 1.3], divorce: [0.4, 2.5], remarriage: [0.5, 2.5] };
export const clampFactor = (type, m) => { const [lo, hi] = FACTOR_RANGE[type] ?? FACTOR_RANGE.default; return Math.max(lo, Math.min(hi, m)); };
/** 그 해 그 사건이 일어난 이유를 쉬운 말로 돌려준다(결과 화면용). opens 는 그 나이의 열린 영역 배열이다. */
export function lifeReasons(s, age, type, opens = []) {
  const sex = s.birth?.gender === 'F' ? 'F' : 'M', out = [];
  const link = LINK[type]?.[sex];
  if (link && opens.includes(link)) out.push(`그해 ${GROUP_LABEL[link]} 기회가 열려 있었다`);
  const luck = luckAt(s.saju, age), eff = TRANSITION_EFFECT[type];
  if (eff && luck.inTransition && getTransitionTable()) {
    const kind = classifyTransition(essenceFor(s.saju), luck);
    if (kind && eff[kind]) out.push(`대운이 바뀌는 시기(${kind} 유형)였다`);
  }
  if (type === 'marriage' || type === 'divorce' || type === 'remarriage') {
    const palace = s.saju?.day?.branch, seun = BRANCH_MAIN_YEAR(s.birthYear + age), dw = luck.daewoon?.branch;
    if (Number.isInteger(palace)) {
      const branches = [seun, dw].filter(Number.isInteger);
      if (branches.some((b) => isHap(palace, b))) out.push('배우자 자리(일지)가 그해 운과 합을 이뤘다');
      if (type !== 'remarriage' && branches.some((b) => isClash(palace, b))) out.push('배우자 자리(일지)가 그해 운과 부딪혔다(충·형)');
    }
  }
  if (type === 'divorce') {
    const share = essenceFor(s.saju)?.groupShare?.value?.[SPOUSE_GROUP[sex]];
    if (Number.isFinite(share) && (share >= SPOUSE_STAR_HIGH || share <= SPOUSE_STAR_LOW)) out.push(share >= SPOUSE_STAR_HIGH ? '사주에 배우자 영역이 매우 많다' : '사주에 배우자 영역이 거의 없다');
  }
  return out;
}

const normOf = (type, sex) => tables.norm?.[type]?.[sex] ?? 1;
// 정규화: raw/norm 을 범위로 자른 값의 평균이 1이 되도록 norm 을 맞춰 두었다(calibrate-life.mjs).
const factor = (s, age, type, opens) => clampFactor(type, rawFactor(s, age, type, opens) / normOf(type, s.birth?.gender === 'F' ? 'F' : 'M'));

// ───────── 통계 표 읽기 ─────────
const decadeKey = (age) => (age < 30 ? '20s' : age < 40 ? '30s' : age < 50 ? '40s' : age < 60 ? '50s' : age < 70 ? '60s' : '70s');
const band5 = (age) => `${Math.floor(age / 5) * 5}-${Math.floor(age / 5) * 5 + 4}`;
const healthBand = (age) => (age >= 80 ? '80+' : band5(age));
const healthP = (age) => tables.health.byBand[healthBand(age)] ?? 0;
const petBand = (age) => (age < 30 ? '20' : age < 40 ? '30' : age < 50 ? '40' : age < 60 ? '50' : '60+');
const marriageRate = (age, sex) => (age < 20 || age >= 75 ? 0 : tables.firstMarriage[band5(age)]?.[sex] ?? 0);
const birthRate = (age) => (age < 20 || age >= 50 ? 0 : tables.birth[band5(age)] ?? 0);
const MAX_CHILDREN = 3, MAX_HOMES = 3;

/**
 * 그 나이의 인생 사건 판정. s 는 바꾸지 않고, 상태 전이가 반영된 life 와 사건 목록을 돌려준다.
 * 반환: { life, response, record, dropped }. response 는 선택 이벤트로 올릴 사건, record 는 자동 기록으로 처리할 사건.
 */
export function rollLife(s, age, opens = []) {
  const life = structuredClone(s.life ?? initLife());
  const sex = s.birth?.gender === 'F' ? 'F' : 'M';
  const out = { life, response: null, record: null, dropped: [] };
  if (age < 15) return out;
  const candidates = [];
  const draw = (type, p) => lifeRng(s, age, SALT[type])() < Math.min(0.5, Math.max(0, p));
  const stateRng = lifeRng(s, age, SALT.state);

  // 상태 전이(고액 의료비 · 임금근로 · 반려동물 보유)
  const D = tables.health.D, x = 1 / D;
  if (life.health.state === null) { life.health.state = stateRng() < healthP(age) ? 1 : 0; life.health.since = life.health.state ? age : null; }
  else if (life.health.state === 1) { if (stateRng() < x) { life.health.state = 0; life.health.since = null; } }
  else {
    const prevP = healthP(age - 1), lam = Math.min(1, Math.max(0, (healthP(age) - prevP * (1 - x)) / (1 - prevP)));
    if (draw('illness', lam)) { life.health.state = 1; life.health.since = age; candidates.push({ type: 'illness', m: 1 }); }
  }
  if (age >= 24 && age < 60) {
    const dec = tables.jobChange.decades[decadeKey(age)], e = 1 / tables.jobChange.L;
    if (life.work.state === null) life.work.state = lifeRng(s, age, 221)() < tables.jobChange.decades['20s'].share ? 'E' : 'O';
    else if (life.work.state === 'E') { if (lifeRng(s, age, 222)() < e) life.work.state = 'O'; }
    else if (lifeRng(s, age, 222)() < Math.min(1, (dec.share / (1 - dec.share)) * e)) life.work.state = 'E';
    if (life.work.state === 'E') {
      const m = factor(s, age, 'jobChange', opens);
      // 직업이 있고 일을 시작한 지 2년 이상이며, 직업 전환 제안은 8년에 한 번까지만 나온다(게임 추정)
      if (s.job && age >= 26 && age - (life.lastOffer?.jobChange ?? -99) >= 8 && draw('jobChange', dec.sep * m)) candidates.push({ type: 'jobChange', m });
    }
  }
  if (age >= 20) {
    const pb = tables.pet[petBand(age)];
    life.pet.list ??= Array.from({ length: petCount(life) }, () => ({ adopted: age - 1, lifespan: petLifespan(0.5) })); // 옛 기록 보충
    if (life.pet.owned === null) { // 20세 시작: 통계의 양육 비율만큼 이미 키우고 있다. 키운 햇수는 수명 안에서 무작위.
      if (lifeRng(s, age, 223)() < tables.pet['20'].share) { const lifespan = petLifespan(lifeRng(s, age, 225)()); life.pet.list = [{ adopted: age - Math.floor(lifeRng(s, age, 226)() * lifespan), lifespan }]; }
      syncPets(life);
    } else {
      // 수명이 다한 반려동물은 기록으로만 남는다(선택 없음)
      const alive = life.pet.list.filter((p) => age < p.adopted + p.lifespan);
      out.petDeaths = life.pet.list.length - alive.length;
      if (out.petDeaths) { life.pet.list = alive; syncPets(life); }
      if (!out.petDeaths && petCount(life) < MAX_PETS && (life.pet.offers ?? 0) < MAX_PET_OFFERS) { // 이미 키우고 있으면 한 마리 더 들일 기회는 훨씬 드물다(게임 추정 ×0.15)
        const m = factor(s, age, 'pet', opens), base = pb.start * (petCount(life) === 0 ? 1 : 0.15);
        if (age - (life.lastOffer?.pet ?? -99) >= PET_COOLDOWN && draw('pet', base * m)) candidates.push({ type: 'pet', m });
      }
    }
    if (age < 80 && life.home.count < MAX_HOMES) {
      const m = factor(s, age, 'house', opens);
      if (offerOk(life, 'house', age) && draw('house', (tables.housing[decadeKey(age)] ?? 0) * m)) candidates.push({ type: 'house', m });
    }
    const status = life.marriage.status;
    if (status === 'single') {
      const m = factor(s, age, 'marriage', opens);
      if (offerOk(life, 'marriage', age) && draw('marriage', marriageRate(age, sex) * m)) candidates.push({ type: 'marriage', m });
    } else if (status === 'married') {
      if (life.marriage.divorces < tables.divorce.cap) {
        const m = factor(s, age, 'divorce', opens);
        if (offerOk(life, 'divorce', age) && draw('divorce', tables.divorce.base * m)) candidates.push({ type: 'divorce', m });
      }
      if (life.children.length < MAX_CHILDREN) {
        const m = factor(s, age, 'birth', opens);
        // 출산 제안은 한 번 나오면(거절·출산 모두) 4년간 다시 나오지 않고, 둘째·셋째는 확률이 낮다(게임 추정)
        const order = [1, 0.6, 0.25][life.children.length] ?? 0.25;
        if (offerOk(life, 'birth', age) && draw('birth', birthRate(sex === 'F' ? age : age - 2) * m * order)) candidates.push({ type: 'birth', m });
      }
    } else if (status === 'divorced') {
      const m = factor(s, age, 'remarriage', opens);
      if (offerOk(life, 'remarriage', age) && draw('remarriage', tables.remarriage.base * m)) candidates.push({ type: 'remarriage', m });
    }
  }
  candidates.sort((a, b) => (b.m - a.m) || (PRIORITY.indexOf(a.type) - PRIORITY.indexOf(b.type)));
  out.response = candidates[0] ?? null;
  out.record = candidates.length > 1 && MAX_PER_YEAR > 1 ? candidates[1] : null;
  out.dropped = candidates.slice(MAX_PER_YEAR);
  return out;
}

/** 행동(act)이 성공했을 때 인생 상태를 바꾼다. 질병과 직업 전환은 상태 전이가 따로 있어 바꾸지 않는다. */
export function applyLifeAct(life, type, age, roll = 0.5) {
  if (type === 'marriage') life.marriage = { ...life.marriage, status: 'married', at: age };
  else if (type === 'remarriage') life.marriage = { ...life.marriage, status: 'married', at: age };
  else if (type === 'divorce') life.marriage = { ...life.marriage, status: 'divorced', divorces: (life.marriage.divorces ?? 0) + 1 };
  else if (type === 'birth') life.children.push({ bornAge: age });
  else if (type === 'house') { life.home.count += 1; life.home.boughtAge = age; }
  else if (type === 'pet') { life.pet.list ??= []; life.pet.list.push({ adopted: age, lifespan: petLifespan(roll) }); life.pet.list = life.pet.list.slice(-MAX_PETS); syncPets(life); }
  life.occurrences.push({ age, type });
}

export const lifeRecordText = (type) => LIFE_RECORD_TEXT[type];

/** 선택 이벤트로 올릴 인생 사건을 이벤트 형식으로 만든다. 없으면 null. prepare 는 events.js 의 prepareEvent 이다. */
export function lifeEventFor(s, opens, prepare) {
  if (s.lifeModel !== LIFE_MODEL || !s.life || s.age < 15) return null;
  const roll = rollLife(s, s.age, opens);
  const type = roll.response?.type;
  if (!type) return null;
  const c = LIFE_EVENT_CONTENT[type];
  const morePet = type === 'pet' && petCount(s.life) > 0;
  const event = { id: `life_${type}`, category: 'life', lifeType: type, field: c.field, ageMin: 15, ageMax: 120, baseWeight: 1,
    cooldownYears: 0, contentVersion: 1, title: morePet ? `반려동물을 한 마리 더 들일까? (지금 ${petCount(s.life)}마리)` : c.title,
    description: morePet ? '함께 사는 동물이 있는데 한 마리를 더 맡아 달라는 이야기가 나온다. 돌봄 시간과 비용이 마리 수만큼 늘어난다.' : c.description, rules: {}, choices: structuredClone(c.choices) };
  if (type === 'house') { // 집값·대출·내 돈을 보여 준다(금액은 R-ONE 평균 매매가와 연령대별 평균 주담대 신규취급액)
    const { price, loan, equity } = housePurchase(s, s.age), cash = Math.max(0, Math.round(s.life.economy?.cash ?? 0));
    event.description += `
집값 ${fmtMoney(price)} · 대출 ${fmtMoney(loan)} · 내 돈 ${fmtMoney(equity)}${cash < equity ? ` (지금 현금 ${fmtMoney(cash)}, 모자란 만큼은 빚이 됩니다)` : ` (지금 현금 ${fmtMoney(cash)})`}`;
    event.choices[0].hint = `집값 ${fmtMoney(price)} · 대출 ${fmtMoney(loan)} · 내 돈 ${fmtMoney(equity)}`;
  }
  event.description = fillMoney(event.description, s, s.age);
  for (const c of event.choices) c.hint = fillMoney(c.hint, s, s.age);
  return prepare ? prepare(s, event) : event;
}
